import React, { useState } from 'react';
import { Modal } from '../common/Modal.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { User } from '../../../shared/types.ts';
import { Users, Search, UserPlus, Trash2, Filter, Check, Mail, Briefcase } from 'lucide-react';
import { MemberProfilePopover } from '../teams/MemberProfilePopover.tsx';

interface ExistingProjectMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  members: User[];
  onAddMemberClick?: () => void;
  onFilterByMember?: (memberId: string) => void;
  onRemoveMember?: (memberId: string) => Promise<void>;
  canManageMembers?: boolean;
}

export const ExistingProjectMembersModal: React.FC<ExistingProjectMembersModalProps> = ({
  isOpen,
  onClose,
  projectName,
  members = [],
  onAddMemberClick,
  onFilterByMember,
  onRemoveMember,
  canManageMembers = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isRemovingId, setIsRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [selectedProfileMember, setSelectedProfileMember] = useState<User | null>(null);

  const filteredMembers = members.filter((m) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (m.fullName && m.fullName.toLowerCase().includes(q)) ||
      (m.email && m.email.toLowerCase().includes(q)) ||
      (m.role && m.role.toLowerCase().includes(q)) ||
      ((m as any).departmentName && (m as any).departmentName.toLowerCase().includes(q))
    );
  });

  const handleRemove = async (memberId: string) => {
    if (!onRemoveMember) return;
    if (!confirm('Are you sure you want to remove this member from the project?')) return;
    try {
      setIsRemovingId(memberId);
      setRemoveError(null);
      await onRemoveMember(memberId);
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove member.');
    } finally {
      setIsRemovingId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-surface-900 dark:text-surface-100 truncate">
              Existing Project Members
            </div>
            <div className="text-[11px] font-normal text-surface-500 dark:text-surface-400 truncate">
              {projectName} • {members.length} {members.length === 1 ? 'member' : 'members'}
            </div>
          </div>
        </div>
      }
      maxWidth="md"
    >
      <div className="space-y-4">
        {/* Actions bar: Search + Add Member Button */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search existing members by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {onAddMemberClick && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onAddMemberClick();
              }}
              className="px-3 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold shadow-2xs flex items-center space-x-1.5 transition-colors shrink-0"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Add Member</span>
            </button>
          )}
        </div>

        {removeError && (
          <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-xs text-rose-600 font-medium">
            {removeError}
          </div>
        )}

        {/* Members List */}
        <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
          {members.length === 0 ? (
            <div className="py-10 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-surface-100 dark:bg-surface-800 text-surface-400 mx-auto flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-surface-800 dark:text-surface-200">
                  No specific members attached to this project
                </p>
                <p className="text-[11px] text-surface-400 max-w-xs mx-auto">
                  By default, all workspace team members have access to collaborate on this board.
                </p>
              </div>
              {onAddMemberClick && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onAddMemberClick();
                  }}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold shadow-2xs transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>Attach Specific Members</span>
                </button>
              )}
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-8 text-center text-xs text-surface-400 space-y-1">
              <p>No project members match "{searchQuery}".</p>
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-brand-600 hover:text-brand-700 underline text-xs font-semibold"
              >
                Clear search filter
              </button>
            </div>
          ) : (
            filteredMembers.map((m) => (
              <div
                key={m.id}
                onClick={() => setSelectedProfileMember(m)}
                className="p-3 rounded-xl bg-surface-50 dark:bg-surface-800/70 border border-surface-200/70 dark:border-surface-700/70 flex items-center justify-between text-xs transition-all hover:border-brand-500/40 hover:bg-surface-100/70 dark:hover:bg-surface-800 cursor-pointer group/memberCard"
                title={`Click to view ${m.fullName}'s profile`}
              >
                <div className="flex items-center space-x-3 min-w-0 pr-2">
                  <div className="relative shrink-0 transition-transform group-hover/memberCard:scale-105">
                    <Avatar src={m.avatarUrl} name={m.fullName} size="sm" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center space-x-1.5">
                      <span className="font-semibold text-surface-900 dark:text-surface-100 truncate group-hover/memberCard:text-brand-600 dark:group-hover/memberCard:text-brand-400 transition-colors">
                        {m.fullName}
                      </span>
                      {m.role && (
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-surface-200/70 dark:bg-surface-700 text-surface-600 dark:text-surface-300">
                          {m.role}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3 text-[11px] text-surface-400 truncate mt-0.5">
                      {m.email && (
                        <span className="flex items-center space-x-1 truncate">
                          <Mail className="w-3 h-3 text-surface-400 shrink-0" />
                          <span className="truncate">{m.email}</span>
                        </span>
                      )}
                      {(m as any).departmentName && (
                        <span className="flex items-center space-x-1 truncate">
                          <Briefcase className="w-3 h-3 text-surface-400 shrink-0" />
                          <span className="truncate">{(m as any).departmentName}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center space-x-1.5 shrink-0 ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {onFilterByMember && (
                    <button
                      type="button"
                      onClick={() => {
                        onFilterByMember(m.id);
                        onClose();
                      }}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-white dark:bg-surface-700 text-surface-700 dark:text-surface-200 border border-surface-200 dark:border-surface-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center space-x-1"
                      title="Filter board cards by this member"
                    >
                      <Filter className="w-3 h-3" />
                      <span className="hidden sm:inline">Filter Cards</span>
                    </button>
                  )}

                  {canManageMembers && onRemoveMember && (
                    <button
                      type="button"
                      disabled={isRemovingId === m.id}
                      onClick={() => handleRemove(m.id)}
                      className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors disabled:opacity-50"
                      title="Remove from project"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Interactive Member Profile Popover */}
      {selectedProfileMember && (
        <MemberProfilePopover
          member={selectedProfileMember}
          teamName={projectName}
          onClose={() => setSelectedProfileMember(null)}
          onViewAssignedTasks={
            onFilterByMember
              ? (m) => {
                  setSelectedProfileMember(null);
                  onFilterByMember(m.id);
                  onClose();
                }
              : undefined
          }
        />
      )}
    </Modal>
  );
};
